import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Volume2, VolumeX, Radiation, HelpCircle } from 'lucide-react';
import MapView2D from '@/components/MapView2D';
import SplashScreen from '@/components/SplashScreen';
import IntroSequence from '@/components/IntroSequence';
import ExitScreen from '@/components/ExitScreen';
import DiplomacyAnimation from '@/components/DiplomacyAnimation';
import AADefenseEffect from '@/components/AADefenseEffect';
import AAPurchasePopup from '@/components/AAPurchasePopup';
import BlackmailAnimation from '@/components/BlackmailAnimation';
import DeployTroopsAnimation from '@/components/DeployTroopsAnimation';
import AirStrikeAnimation from '@/components/AirStrikeAnimation';
import DeployTroopsConfirmation from '@/components/DeployTroopsConfirmation';
import NukeReadyCelebration from '@/components/NukeReadyCelebration';
import NuclearStrikeAnimation from '@/components/NuclearStrikeAnimation';
import NukeButtonAnimation from '@/components/NukeButtonAnimation';
import EnrichmentPurchasePopup from '@/components/EnrichmentPurchasePopup';
import StatusReportPopup from '@/components/StatusReportPopup';
import EnrichmentMeterAnimation from '@/components/EnrichmentMeterAnimation';
import EnrichmentWarningPopup from '@/components/EnrichmentWarningPopup';
import PauseMenu from '@/components/PauseMenu';
import NuclearStatusReport from '@/components/NuclearStatusReport';
import TutorialModal from './TutorialModal';
import AdModal from './AdModal';  // Ad monetization
import ChatBubble from '@/components/ChatBubble';
import AllCountryStatsPanel from '@/components/AllCountryStatsPanel';
import VictoryModal from '@/components/VictoryModal';
import DefeatScreen from '@/components/DefeatScreen';
import IntelligenceReportModal from '@/components/IntelligenceReportModal';
import { ResourceBar, TurnIndicator, CountryPanel, ActionLog, GameNotification, ActionResultModal } from '@/components/GameUI';

// Import custom hooks
import { useKeydownListener } from '@/hooks/useKeydownListener';
import { useBackgroundMusic } from '@/hooks/useBackgroundMusic';
import { useNuclearEffects } from '@/hooks/useNuclearEffects';
import useMapScrollLeft from '@/hooks/useMapScrollLeft';
import { useGameActions } from '@/hooks/useGameActions';

function GameScreen({ game, setGame, onAction, onEndTurn, onBackToLobby, notification, setNotification, showNotification, isLoadedGame = false }) {
  // ========== GAME ACTIONS HOOK ==========
  const {
    handleAction: hookHandleAction,
    handleEndTurn: hookHandleEndTurn,
    skipAiTurns,  // ⭐ New function from hook
    fetchLatestGameState,  // ⭐ Refresh game state
    isProcessing,
    isAirStrikeLocked,  // 🔒 Map READ-ONLY during air strike
    lastAction,
    actionResult,
    isActionModalOpen,
    setIsActionModalOpen,
    propagandaAnimation,
    diplomacyAnimation,
    blackmailAnimation,
    airStrikeAnimation,
    deployTroopsAnimation,
    setDeployTroopsAnimation,  // ⭐ Setter for manual animation triggering
    nukeButtonAnimation,  // ⭐ NEW: From hook now (not local state)
    handleNukeButtonComplete,  // ⭐ NEW: Handler from hook
    nuclearStrikeAnimation,
    bomberFlash,  // ⭐ New: bomber flash animation
    deployTroopsConfirmation,
    setDeployTroopsConfirmation,
    enrichmentPopup,  // ⭐ AI enrichment purchase popup
    setEnrichmentPopup,
    aaDefensePopup,  // ⭐ AA Defense purchase popup
    setAADefensePopup,
    hiddenLeaderCode,
    sweatingLeaders,
    isProcessingAiTurns,
    aiTurnsQueue,  // ⭐ Now received from hook
    aiActionsLog,  // ⭐ AI actions for summary
    setAiActionsLog, // ⭐ Reset at turn start
    activeChatBubbles,
    setLastAction,
    setIsProcessing
  } = useGameActions(game, setGame, onAction, onEndTurn);

  // UI State (not managed by hook)
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showAllCountryStats, setShowAllCountryStats] = useState(false); // New: show all country stats panel
  const [showSplash, setShowSplash] = useState(!isLoadedGame); // Skip splash for loaded games
  const [showIntro, setShowIntro] = useState(false);
  const [showBadgeOverlay, setShowBadgeOverlay] = useState(false); // Badge overlay on map
  const [showExitScreen, setShowExitScreen] = useState(false); // Exit screen on Save/Surrender
  const [showAADefenseEffect, setShowAADefenseEffect] = useState(false); // AA firing animation
  const [aaEffectTarget, setAAEffectTarget] = useState(null); // Plane position for AA targeting
  const [aaEffectGuns, setAAEffectGuns] = useState(null); // AA gun positions
  const [aaEffectDefendingCountry, setAAEffectDefendingCountry] = useState(null); // Defending country code for AA missiles
  const [aaEffectTier, setAAEffectTier] = useState(1); // AA tier for missile count
  const [enrichmentAnimation, setEnrichmentAnimation] = useState(null);
  
  // ========== VIDEO PRELOADING ==========
  // Preload critical videos to reduce lag during gameplay
  useEffect(() => {
    const videosToPreload = [
      // Diplomacy videos (common)
      '/assets/diplomacy-handshake.mp4',
      '/assets/diplomacy-handshake-2.mp4',
      '/assets/diplomacy-handshake-3.mp4',
      
      // Ejection videos (for failed air strikes)
      '/assets/ejection-usa.mp4',
      '/assets/ejection-china.mp4',
      '/assets/ejection-eu.mp4',
      '/assets/ejection-russia.mp4',
      '/assets/ejection-nk.mp4',
      
      // Deploy troops videos (all 6 variants)
      '/assets/deploy-troops-video.mp4',
      '/assets/deploy-troops-video-2.mp4',
      '/assets/deploy-troops-video-3-bombing.mp4',
      '/assets/deploy-troops-video-4-chopper.mp4',
      '/assets/deploy-troops-video-5-carrier.mp4',
      '/assets/carrier-takeoff.mov',
    ];

    console.log('🎬 Preloading critical videos...');
    const preloadedVideos = [];

    videosToPreload.forEach((src) => {
      const video = document.createElement('video');
      video.src = src;
      video.preload = 'auto';
      video.load();
      preloadedVideos.push(video);
    });

    console.log(`✅ Initiated preload for ${videosToPreload.length} videos`);

    // Cleanup
    return () => {
      preloadedVideos.forEach(video => {
        video.src = '';
      });
    };
  }, []); // Run once on mount
  
  // Popup States (not managed by hook)
  const [nukeReadyCelebration, setNukeReadyCelebration] = useState(null);
  // ⭐ REMOVED: nukeButtonAnimation now managed in hook
  const [showStatusReport, setShowStatusReport] = useState(false);
  const [showDefeatScreen, setShowDefeatScreen] = useState(false); // NEW: Defeat screen
  const [isSpectating, setIsSpectating] = useState(false); // Prevent defeat screen from re-showing
  const [enrichmentWarning, setEnrichmentWarning] = useState(null);
  const [showPauseMenu, setShowPauseMenu] = useState(false);
  const [showNuclearStatusReport, setShowNuclearStatusReport] = useState(false);
  const [isPendingNuclearStrike, setIsPendingNuclearStrike] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false); // TUTORIAL STATE
  const [showTutorialHint, setShowTutorialHint] = useState(true); // New: show hint arrow
  const [showIntelligenceReport, setShowIntelligenceReport] = useState(false); // Intelligence modal
  const [intelligenceData, setIntelligenceData] = useState(null); // Intelligence report data
  
  // Victory modal state
  const [victoryData, setVictoryData] = useState(null); // { defeatedCountry, stats }
  
  // ✅ AD MONETIZATION STATE
  const [showAdModal, setShowAdModal] = useState(false);
  const [adType, setAdType] = useState('endgame'); // 'endgame' or 'midgame'
  const [lastAdTurn, setLastAdTurn] = useState(0); // Track last turn ad was shown
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(() => {
    // Check localStorage to see if user has played before
    const hasPlayedBefore = localStorage.getItem('gtnw3_has_played');
    return !hasPlayedBefore;
  });
  
  // Initialize defeated countries from game data
  const [defeatedCountries, setDefeatedCountries] = useState(() => {
    // Check if any countries have is_eliminated flag or eliminated flag
    if (game && game.countries) {
      return game.countries
        .filter(c => c.is_eliminated || c.eliminated || c.resources?.eliminated || (c.resources?.population || 0) <= 0)
        .map(c => c.code);
    }
    return [];
  });

  // ✅ SYNC defeated countries with game state (runs whenever game data changes)
  // Also detects NEW eliminations and fires victory celebration if the
  // player was responsible. This catches eliminations that don't come
  // from an instant nuke kill — e.g., a country dies of radiation 2-3
  // turns AFTER the nuke, during AI turn processing.
  const prevDefeatedRef = useRef(new Set());
  useEffect(() => {
    if (!game || !game.countries) return;

    const eliminatedNow = game.countries
      .filter(c => c.is_eliminated || c.eliminated || c.resources?.eliminated || (c.resources?.population || 0) <= 0)
      .map(c => c.code);
    setDefeatedCountries(eliminatedNow);

    // Find NEW eliminations since last render
    const newlyDefeated = eliminatedNow.filter(code => !prevDefeatedRef.current.has(code));
    prevDefeatedRef.current = new Set(eliminatedNow);

    if (newlyDefeated.length === 0) return;
    if (!game.player_country) return;
    if (showDefeatScreen) return; // player's defeat takes precedence

    // Fire victory celebration for the FIRST new elimination credited to the player.
    // (If multiple died at once, we celebrate them sequentially on subsequent renders.)
    for (const code of newlyDefeated) {
      const defeated = game.countries.find(c => c.code === code);
      if (!defeated) continue;
      if (code === game.player_country) continue; // player's own death is handled by defeat screen
      if (defeated.eliminated_by !== game.player_country) continue; // someone else got the kill

      const playerCountry = game.countries.find(c => c.code === game.player_country);
      console.log('🏆 PLAYER ELIMINATED', defeated.name, '— firing victory celebration');
      setVictoryData({
        defeatedCountry: {
          countryCode: defeated.code,
          countryName: defeated.name,
          leaderName: defeated.leader_name,
          leaderTitle: defeated.leader_title || 'Leader',
        },
        playerCountry: playerCountry ? {
          code: playerCountry.code,
          name: playerCountry.name,
          stars: playerCountry.victory_stars || 0,
        } : null,
        stats: {},
      });
      break;
    }
  }, [game, showDefeatScreen]);
  
  // ✅ DEFEAT DETECTION - Check if player is eliminated (runs on game updates)
  useEffect(() => {
    if (!game || !game.player_country) return;
    
    const playerCountry = game.countries?.find(c => c.code === game.player_country);
    if (!playerCountry) return;
    
    // Check if player is defeated (multiple conditions)
    const isPlayerDefeated = 
      playerCountry.eliminated ||
      playerCountry.is_eliminated ||
      playerCountry.status === 'defeated' ||
      (playerCountry.resources?.population || 0) <= 0;
    
    // ⚠️ Don't show defeat screen if player chose to spectate
    if (isPlayerDefeated && !showDefeatScreen && !isSpectating) {
      console.log('💀 PLAYER DEFEATED DETECTED', {
        eliminated: playerCountry.eliminated,
        status: playerCountry.status,
        population: playerCountry.resources?.population,
        nuclearAnimationActive: !!nuclearStrikeAnimation
      });
      
      // ⚠️ CINEMATIC FIX: Don't show defeat screen during nuclear animation!
      // Wait for the full mushroom cloud cinematic to complete (12+ seconds)
      const delayBeforeDefeat = nuclearStrikeAnimation ? 13000 : 1000;
      
      setTimeout(() => {
        // Double-check they didn't start spectating during the delay
        if (!isSpectating) {
          setShowDefeatScreen(true);
          showNotification(
            '💀 GAME OVER: Your nation has fallen.',
            'danger'
          );
        }
      }, delayBeforeDefeat);
    }
  }, [game, showDefeatScreen, showNotification, nuclearStrikeAnimation, isSpectating]);
  
  // Audio State
  // 📱 Music starts MUTED on mobile by default. HTML5 Audio on iOS WebKit
  // only allows ~1 concurrent channel — when music plays, dramatic SFX
  // (sirens, bombs, voiceovers) silently fail. Mobile users can tap the
  // top-right speaker icon to enable music if they want it. Desktop
  // continues to start unmuted.
  const [isMusicMuted, setIsMusicMuted] = useState(() => {
    if (typeof window === 'undefined') return false;
    const isMobile = window.innerWidth < 768;
    return isMobile;
  });
  
  // Refs
  const welcomePlayedRef = useRef(false);
  
  const playerCountry = game.countries.find(c => c.is_player);
  
  // ========== WRAPPER FUNCTIONS FOR ACTIONS ==========
  
  // Wrapper for handleAction to pass handleEndTurn correctly
  const handleAction = useCallback(async (actionType, targetCode) => {
    // Handle install_radar action separately (doesn't need target)
    if (actionType === 'install_radar') {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        const playerCode = game.countries.find(c => c.is_player)?.code;
        
        const response = await fetch(`${API_URL}/api/games/${game.id}/install-radar?source_country=${playerCode}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (!response.ok) {
          const error = await response.json();
          showNotification(error.detail || 'Radar installation failed', 'danger');
          return;
        }
        
        const data = await response.json();
        
        // Show success notification
        showNotification(data.message, 'success');
        
        // Update game state locally (mark radar as installed)
        setGame(prev => ({
          ...prev,
          countries: prev.countries.map(c => 
            c.is_player 
              ? { 
                  ...c, 
                  has_radar: true,
                  resources: { 
                    ...c.resources, 
                    money: c.resources.money - data.cost 
                  },
                  actions_remaining: c.actions_remaining - 1
                }
              : c
          )
        }));
        
        return data;
      } catch (error) {
        console.error('Radar installation error:', error);
        showNotification('Failed to install radar', 'danger');
      }
      return;
    }
    
    // Close country panel for air strikes (no popups during animation)
    if (actionType === 'air_strike') {
      setSelectedCountry(null);
    }
    
    return await hookHandleAction(actionType, targetCode, hookHandleEndTurn);
  }, [hookHandleAction, hookHandleEndTurn, game, setGame, showNotification, isProcessingAiTurns]);

  // ========== SURRENDER HANDLER ==========
  
  const handleSurrender = useCallback(async () => {
    try {
      console.log('🏳️ Player surrendering game...');
      
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${API_URL}/api/games/${game.id}/surrender`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      // ⭐ FIX: Read response.json() only ONCE and store it
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to surrender');
      }
      
      console.log('🏳️ Surrender successful:', data);
      
      // Update game state
      setGame(data.game);
      
      // Close pause menu
      setShowPauseMenu(false);
      
      // ⭐ Show exit screen before returning to lobby
      setShowExitScreen(true);
      
      // After exit screen (10s or ESC), show ad and defeat screen
      setTimeout(() => {
        setShowExitScreen(false);
        // Show ad before defeat screen (for non-first-time users)
        showAdAfterGame('endgame');
        // Show defeat screen (will show after ad if applicable)
        setShowDefeatScreen(true);
      }, 10000);
      
      showNotification('🏳️ You have surrendered. The war is over.', 'danger');
    } catch (error) {
      console.error('❌ Surrender failed:', error);
      showNotification(`❌ Failed to surrender: ${error.message}`, 'danger');
    }
  }, [game?.id, setGame, showNotification]);

  // ✅ SHOW AD AFTER GAME ENDS (for non-first-time users)
  const showAdAfterGame = useCallback((type = 'endgame') => {
    // Skip ads for first-time users
    if (isFirstTimeUser) {
      console.log('🆕 First-time user - skipping ad');
      // Mark user as having played
      localStorage.setItem('gtnw3_has_played', 'true');
      setIsFirstTimeUser(false);
      return;
    }

    // Show ad modal
    setAdType(type);
    setShowAdModal(true);
  }, [isFirstTimeUser]);

  // Check if we should show mid-game ad (every 20 turns)
  useEffect(() => {
    if (!game || isFirstTimeUser || isLoadedGame) return; // ⭐ Skip auto-trigger for loaded games

    const currentTurn = game.current_turn || 0;
    
    // Show ad every 20 turns (turn 20, 40, 60, etc.)
    if (currentTurn > 0 && currentTurn % 20 === 0 && currentTurn !== lastAdTurn) {
      console.log(`📺 Showing mid-game ad at turn ${currentTurn}`);
      setLastAdTurn(currentTurn);
      showAdAfterGame('midgame');
    }
  }, [game?.current_turn, isFirstTimeUser, lastAdTurn, showAdAfterGame, isLoadedGame]); // ⭐ Added isLoadedGame

  
  // Handle country clicks - check for pending nuclear strike
  const handleCountryClick = useCallback((country) => {
    // Block ALL clicks during AI turn processing
    if (isProcessingAiTurns) {
      console.log('🔒 Click blocked - AI is taking their turn');
      showNotification('⏳ Please wait for AI to finish their turns', 'warning');
      return;
    }
    
    // Block clicks during nuclear strike animation
    if (nuclearStrikeAnimation) {
      console.log('🔒 Click blocked - Nuclear strike in progress');
      return;
    }
    
    // Block clicks during air strike
    if (isAirStrikeLocked) {
      console.log('🔒 Click blocked - Air strike in progress');
      return;
    }
    
    // If we're in nuclear strike mode, launch the nuke at this country
    if (isPendingNuclearStrike) {
      console.log('💥 Nuclear strike target selected:', country.name);
      setIsPendingNuclearStrike(false);
      
      // Check if player has nukes ready OR enrichment at 100%
      const canLaunch = (playerCountry.resources.nukes_ready > 0) || (playerCountry.resources.uranium_enrichment >= 100);
      
      if (canLaunch) {
        // ⭐ Trigger nuclear strike - the hook will handle button animation → missile animation
        console.log('🔴 Launching nuclear strike at', country.code);
        handleAction('nuclear_strike', country.code);
      } else {
        const enrichment = playerCountry.resources.uranium_enrichment || 0;
        showNotification(`❌ Uranium enrichment not ready! Currently at ${enrichment.toFixed(0)}%. Need 100% to launch.`, 'danger');
      }
      return;
    }
    
    // Normal country selection - show properties window
    setSelectedCountry(country);
  }, [isProcessingAiTurns, isPendingNuclearStrike, playerCountry, handleAction, showNotification, isAirStrikeLocked]);
  
  // ⭐ REMOVED: handleNukeButtonComplete now handled in useGameActions hook
  
  // Wrapper for handleEndTurn to handle nuke celebrations
  const handleEndTurn = useCallback(async () => {
    const result = await hookHandleEndTurn();
    
    // Handle nuke ready celebration if needed
    if (result && result.nukeReady) {
      setNukeReadyCelebration(result.nukeReady);
    }
    
    // ☠️ CHECK FOR ELIMINATIONS (500k population threshold)
    if (result && result.eliminated && result.eliminated.length > 0) {
      for (const eliminated of result.eliminated) {
        // Show dramatic defeat message
        showNotification(
          `☠️ ${eliminated.name} HAS FALLEN! ${eliminated.leader} defeated - population below 500k!`,
          'danger'
        );
        
        // Check if PLAYER was eliminated
        const playerCountry = game?.countries?.find(c => c.code === game?.player_country);
        if (eliminated.code === playerCountry?.code) {
          // PLAYER DEFEATED! Show defeat screen with options
          console.log('💀 PLAYER ELIMINATED - Showing defeat screen');
          setTimeout(() => {
            setShowDefeatScreen(true);
            showNotification(
              '💀 GAME OVER: Your nation has fallen. Choose to spectate or return to lobby.',
              'danger'
            );
          }, 2000);
        }
      }
    }
    
    return result;
  }, [hookHandleEndTurn, game, showNotification]);
  
  // ========== CUSTOM HOOKS ==========
  
  // ESC key handler
  useKeydownListener(
    {
      deployTroopsConfirmation,
      enrichmentWarning,
      showStatusReport,
      nukeReadyCelebration,
      showPauseMenu,
      showNuclearStatusReport
    },
    {
      setDeployTroopsConfirmation,
      setEnrichmentWarning,
      setShowStatusReport,
      setNukeReadyCelebration,
      setShowPauseMenu
    }
  );
  
  // Background music
  useBackgroundMusic(showSplash, showIntro, isMusicMuted, {
    propagandaAnimation,
    diplomacyAnimation,
    blackmailAnimation,
    deployTroopsAnimation,
    nuclearStrikeAnimation,
    airStrikeAnimation,
    nukeReadyCelebration
  });
  
  // Nuclear effects (enrichment warnings, status reports)
  useNuclearEffects(game, setEnrichmentWarning, setShowNuclearStatusReport, isProcessingAiTurns);

  // 📱 Mobile portrait: track map horizontal scroll so the bottom-left
  // panels (Intelligence Feed + Country Stats) can stay anchored to the
  // LEFT edge of the map. They'll slide off-screen as the player scrolls
  // right, freeing the limited mobile real estate.
  const { scrollLeft: mapScrollLeft, isMobilePortrait } = useMapScrollLeft();
  
  // ========== EFFECTS ==========
  
  // Show tutorial for first-time players
  useEffect(() => {
    const tutorialCompleted = localStorage.getItem('tutorial_completed');
    if (!tutorialCompleted && !showSplash && !showIntro) {
      setShowTutorial(true);
    }
  }, [showSplash, showIntro]);
  
  // Hide tutorial hint after 5 seconds
  useEffect(() => {
    if (!showSplash && !showIntro) {
      const timer = setTimeout(() => {
        setShowTutorialHint(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSplash, showIntro]);
  
  // Update selected country when game state changes
  useEffect(() => {
    if (selectedCountry) {
      const updatedCountry = game.countries.find(c => c.id === selectedCountry.id);
      if (updatedCountry) {
        setSelectedCountry(updatedCountry);
      }
    }
  }, [game, selectedCountry]);
  
  // Auto-process AI turns when loading a saved game where player's turn is over
  const hasAutoProcessedRef = useRef(false);
  useEffect(() => {
    // Only run once when game first loads with valid player data
    if (!hasAutoProcessedRef.current && game && playerCountry) {
      const actionsRemaining = playerCountry.actions_remaining || 0;
      const currentTurn = game.current_turn || 1;
      
      // If player has no actions left, auto-advance to AI turns (works for ALL turns)
      if (actionsRemaining === 0 && !isProcessingAiTurns && aiTurnsQueue.length === 0) {
        console.log('🔄 Auto-advancing: Player has 0 actions at turn', currentTurn);
        hasAutoProcessedRef.current = true;
        
        // Delay to let splash/intro complete and game screen fully initialize
        setTimeout(() => {
          console.log('🎬 Calling handleEndTurn to process AI actions');
          hookHandleEndTurn();
        }, 2000); // 2 second delay to ensure everything is loaded
      } else {
        // Mark as processed for games that are ready to play
        if (actionsRemaining > 0) {
          console.log(`✅ Player has ${actionsRemaining} actions - waiting for player`);
          hasAutoProcessedRef.current = true;
        }
      }
    }
  }, [game, playerCountry, isProcessingAiTurns, aiTurnsQueue.length, hookHandleEndTurn]);

  // Listen for country defeated events (from useGameActions)
  useEffect(() => {
    const handleCountryDefeated = (event) => {
      console.log('🎉 Country defeated event received:', event.detail);
      
      // Show ad after victory (for non-first-time users)
      showAdAfterGame('endgame');
      
      // Enrich with the current player's country info so VictoryModal can
      // render the happy sprite and accumulated stars.
      const playerCountryObj = game?.countries?.find(c => c.code === game?.player_country);
      setVictoryData({
        ...event.detail,
        playerCountry: playerCountryObj ? {
          code: playerCountryObj.code,
          name: playerCountryObj.name,
          stars: playerCountryObj.victory_stars || 0,
        } : null,
      });
      setDefeatedCountries(prev => [...prev, event.detail.defeatedCountry.countryCode]);
    };
    
    window.addEventListener('countryDefeated', handleCountryDefeated);
    return () => window.removeEventListener('countryDefeated', handleCountryDefeated);
  }, [game]);
  
  // ========== ACTION HANDLERS (Moved to useGameActions hook) ==========
  // All game action logic has been extracted to /app/frontend/src/hooks/useGameActions.js
  // This includes: handleAction, handleEndTurn, AI processing, chat bubbles, animations
  
  const handleCloseActionModal = () => {
    setIsActionModalOpen(false);
    setLastAction(null);
    // Close the country panel as well so it doesn't block other leaders
    setSelectedCountry(null);
  };

  return (
    <>
      {/* Splash Screen */}
      {showSplash && <SplashScreen onComplete={() => {
        setShowSplash(false);
        setShowIntro(true); // Show intro video after splash
      }} />}
      
      {/* Intro Video Sequence with animated title */}
      {showIntro && (
        <IntroSequence 
          onComplete={() => {
            setShowIntro(false);
            setShowBadgeOverlay(true); // Show badge overlay on map
            // Hide badge after 4 seconds
            setTimeout(() => {
              setShowBadgeOverlay(false);
            }, 4000);
          }} 
        />
      )}

      {/* Exit Screen - Shows after Save & Exit or Surrender */}
      {showExitScreen && (
        <ExitScreen 
          onComplete={() => {
            setShowExitScreen(false);
          }} 
        />
      )}
      
      <div className="min-h-screen bg-bunker relative overflow-hidden" data-testid="game-screen">
      {/* 2D Map (background) */}
      <div className="absolute inset-0 z-0">
        <MapView2D
          countries={game.countries}
          selectedCountry={selectedCountry}
          onCountryClick={handleCountryClick}
          lastAction={lastAction}
          isActionModalOpen={isActionModalOpen}
          propagandaAnimation={propagandaAnimation}
          hiddenLeaderCode={hiddenLeaderCode}
          sweatingLeaders={sweatingLeaders}
          defeatedCountries={defeatedCountries}
          activeCountryCode={
            // Reset camera during nuclear strike for full map view
            nuclearStrikeAnimation ? null :
            // During AI turns, zoom in on the active AI country
            isProcessingAiTurns && aiTurnsQueue.length > 0
              ? aiTurnsQueue[0].country_code
              // When it's player turn, show full map (no zoom) - return null to reset camera
              : null
          }
          nuclearStrikeActive={!!nuclearStrikeAnimation}
          nuclearStrikeTargetCode={nuclearStrikeAnimation?.targetCode}  // ⭐ Pass nuke target to hide skull during animation
          airStrikeActive={!!airStrikeAnimation}  // NEW: Pass air strike status to freeze population updates
          currentTurn={game.current_turn}
          isProcessingAiTurns={isProcessingAiTurns}
          game={game}
          setGame={setGame}
        />
      </div>

      {/* ========== MODALS & POPUPS (OUTSIDE pointer-events-none) ========== */}
      
      {/* Tutorial Modal */}
      {showTutorial && (
        <TutorialModal
          onClose={() => setShowTutorial(false)}
          onComplete={() => setShowTutorial(false)}
        />
      )}
      
      {/* Enrichment Warning Popup - 75% and 90% alerts */}
      {enrichmentWarning && (
        <EnrichmentWarningPopup
          country={enrichmentWarning.country}
          enrichmentLevel={enrichmentWarning.enrichmentLevel}
          onClose={() => setEnrichmentWarning(null)}
        />
      )}
      
      {/* UI Overlay */}
      <div className="relative z-10 min-h-screen pointer-events-none">
        {/* Top Right Corner - Country Stats and Controls.
            Wrapper is pointer-events-none so the visually-empty bottom
            of the cluster doesn't block taps to leaders below it. */}
        <div data-testid="game-header-cluster" className="game-header-cluster absolute top-4 right-4 pointer-events-none flex flex-col items-end gap-2 [&>*]:pointer-events-auto">
          {/* Top row: Country Stats, Help, Music, OPTIONS (mobile only) */}
          <div className="flex items-start gap-2">
            {/* 📱 MOBILE OPTIONS BUTTON — replaces ESC keystroke for touch
                devices. Hidden on screens ≥1024px (desktops only). Visible
                on all phone orientations AND iPads. Opens the same
                PauseMenu as ESC. */}
            <button
              data-testid="mobile-options-btn"
              onClick={() => setShowPauseMenu(true)}
              className="lg:hidden bunker-panel p-3 bg-bunker/95 backdrop-blur hover:bg-slate-800 transition-all border-2 border-amber-500 hover:border-amber-400"
              title="Game Options"
              aria-label="Open game options menu"
              style={{ boxShadow: '0 0 14px rgba(245, 158, 11, 0.4)' }}
            >
              <div className="flex items-center gap-2">
                <span className="text-2xl">⚙️</span>
                <div className="text-left">
                  <div className="text-xs font-bold text-amber-400 uppercase tracking-wider">Options</div>
                  <div className="text-[10px] text-amber-300/80">Menu</div>
                </div>
              </div>
            </button>

            {/* Country Stats Button */}
            <button
              onClick={() => setShowAllCountryStats(true)}
              className="bunker-panel p-3 bg-bunker/95 backdrop-blur hover:bg-slate-800 transition-all border border-amber-500/30 hover:border-amber-500"
              title="View All Country Stats"
            >
              <div className="flex items-center gap-2">
                <span className="text-2xl">📊</span>
                <div className="text-left">
                  <div className="text-xs font-bold text-amber-400 uppercase tracking-wider">Country</div>
                  <div className="text-xs text-amber-300">Stats</div>
                </div>
              </div>
            </button>
            
            {/* Help Button for Tutorial */}
            <button
              onClick={() => {
                setShowTutorial(true);
                setShowTutorialHint(false); // Hide hint when clicked
              }}
              className="bunker-panel p-2 bg-bunker/95 backdrop-blur hover:bg-blue-800 transition-all border border-blue-500/30 hover:border-blue-500 relative"
              title="Show Tutorial"
            >
              <HelpCircle className="w-5 h-5 text-blue-400" />
              
              {/* Tutorial Hint - Shows for 5 seconds */}
              {showTutorialHint && (
                <div className="absolute -top-20 -left-4 pointer-events-none animate-bounce">
                  <div className="bg-blue-600 text-white px-3 py-2 rounded-lg shadow-lg font-bold text-sm whitespace-nowrap">
                    Tutorial Available 📚
                    <div className="absolute top-full left-1/2 -translate-x-1/2">
                      <svg width="24" height="24" viewBox="0 0 24 24" className="text-blue-600 drop-shadow-lg">
                        <path fill="currentColor" d="M12 2L2 22h20L12 2z" />
                      </svg>
                    </div>
                  </div>
                </div>
              )}
            </button>
            
            {/* Music Control Button */}
            <button
              onClick={() => setIsMusicMuted(!isMusicMuted)}
              className="bunker-panel p-2 bg-bunker/95 backdrop-blur hover:bg-slate-800 transition-all"
              title={isMusicMuted ? "Unmute Music" : "Mute Music"}
            >
              {isMusicMuted ? (
                <VolumeX className="w-5 h-5 text-red-400" />
              ) : (
                <div className="flex items-center gap-0.5">
                  <Volume2 className="w-5 h-5 text-green-400" />
                  <span className="text-green-400 text-xs">♪</span>
                </div>
              )}
            </button>
          </div>
          
          {/* ⭐ MOVED: Nuclear Threat Gauge - Now below Country Stats to not block victory stars */}
          <div>
          {(() => {
            // Find country with highest enrichment (FILTER OUT DEFEATED COUNTRIES)
            const countriesWithEnrichment = game.countries
              .filter(c => !c.eliminated && !c.is_eliminated)  // ✅ FIX: Don't show defeated countries
              .map(c => ({
                code: c.code,
                name: c.name,
                enrichment: c.resources?.uranium_enrichment || 0
              }))
              .sort((a, b) => b.enrichment - a.enrichment);
            
            // Safety check: Don't show panel if no active countries
            if (countriesWithEnrichment.length === 0) {
              return null;
            }
            
            const topCountry = countriesWithEnrichment[0];
            const enrichmentPercent = Math.round(topCountry.enrichment);
            
            // Color based on threat level
            let gaugeColor = 'bg-green-500';
            let textColor = 'text-green-400';
            let borderColor = 'border-green-500';
            
            if (enrichmentPercent >= 90) {
              gaugeColor = 'bg-red-600';
              textColor = 'text-red-400';
              borderColor = 'border-red-500';
            } else if (enrichmentPercent >= 75) {
              gaugeColor = 'bg-orange-500';
              textColor = 'text-orange-400';
              borderColor = 'border-orange-500';
            } else if (enrichmentPercent >= 50) {
              gaugeColor = 'bg-yellow-500';
              textColor = 'text-yellow-400';
              borderColor = 'border-yellow-500';
            }
            
            return (
              <div className={`bunker-panel p-2 border ${borderColor} bg-bunker/95 backdrop-blur`}>
                <div className="text-[9px] text-muted uppercase tracking-wider mb-1 font-mono">
                  ☢️ Nuclear Threat
                </div>
                <div className={`text-xs font-bold ${textColor} mb-1 font-mono`}>
                  {topCountry.name}
                </div>
                <div className="relative w-24 h-2 bg-neutral-800 rounded-full overflow-hidden border border-neutral-700">
                  <div 
                    className={`absolute top-0 left-0 h-full ${gaugeColor} transition-all duration-500`}
                    style={{ width: `${enrichmentPercent}%` }}
                  />
                </div>
                <div className={`text-sm font-bold ${textColor} text-right mt-1 font-mono`}>
                  {enrichmentPercent}%
                </div>
                {enrichmentPercent >= 100 && (
                  <div className="text-[9px] text-red-500 font-bold mt-0.5 animate-pulse">
                    ⚠️ READY
                  </div>
                )}
              </div>
            );
          })()}
          </div>
        </div>
        
        {/* Nuclear Strike Target Selection Overlay */}
        {isPendingNuclearStrike && (
          <div className="fixed inset-0 z-40 pointer-events-none">
            {/* Pulsing red border */}
            <div className="absolute inset-0 border-8 border-red-500 animate-pulse" />
            
            {/* Top center instruction */}
            <div className="absolute top-1/4 left-1/2 transform -translate-x-1/2 pointer-events-auto">
              <div className="bg-red-600 px-8 py-4 rounded-lg border-4 border-red-400 animate-pulse">
                <h2 
                  className="text-3xl font-black uppercase tracking-wider text-white text-center"
                  style={{
                    fontFamily: "'Orbitron', sans-serif",
                    textShadow: '0 0 20px rgba(255,0,0,0.8), 0 4px 8px rgba(0,0,0,0.9)'
                  }}
                >
                  ☢️ SELECT TARGET COUNTRY ☢️
                </h2>
                <p className="text-white text-center mt-2 text-sm uppercase tracking-wide">
                  Click on any country to launch nuclear strike
                </p>
                <button
                  onClick={() => {
                    setIsPendingNuclearStrike(false);
                    showNotification('Nuclear strike cancelled', 'info');
                  }}
                  className="mt-3 w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded uppercase text-xs tracking-wider"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Diplomacy Animation Overlay */}
        {diplomacyAnimation && (
          <DiplomacyAnimation
            sourceCode={diplomacyAnimation.sourceCode}
            targetCode={diplomacyAnimation.targetCode}
            onComplete={() => {}}
          />
        )}
        
        {/* Bomber Flash - "AirStrike Available" visual */}
        {bomberFlash && (
          <div className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center">
            <div className="animate-pulse">
              <img 
                src={(() => {
                  const code = bomberFlash.countryCode;
                  if (code === 'USA') return '/assets/bombers/b1-bomber-front.png';
                  if (code === 'RUS') return '/assets/bombers/tu-160-front.png';
                  if (code === 'CHN') return '/assets/bombers/china-h6-front.png';
                  if (code === 'EU') return '/assets/bombers/eu-rafale-front.png';
                  if (code === 'NK') return '/assets/bombers/nk-il28-front.png';
                  return '/assets/bombers/b1-bomber-front.png';
                })()}
                alt="Bomber Available"
                className="w-64 h-64 object-contain drop-shadow-2xl"
                style={{
                  filter: 'drop-shadow(0 0 30px rgba(255, 165, 0, 0.8))',
                  animation: 'bomberFlash 3s ease-out'
                }}
              />
            </div>
            <style jsx>{`
              @keyframes bomberFlash {
                0% { opacity: 0; transform: scale(0.5); }
                20% { opacity: 1; transform: scale(1.2); }
                80% { opacity: 1; transform: scale(1); }
                100% { opacity: 0; transform: scale(0.8); }
              }
            `}</style>
          </div>
        )}
        
        {/* Blackmail Animation Overlay */}
        {blackmailAnimation && (
          <BlackmailAnimation
            targetCountry={blackmailAnimation.targetCode}
            gameId={game?.id}
            onComplete={() => setBlackmailAnimation(null)}
          />
        )}
        
        {/* Enrichment Meter Animation - Shows percentage climbing with sound */}
        {enrichmentAnimation && (
          <EnrichmentMeterAnimation
            countryName={enrichmentAnimation.countryName}
            fromPercent={enrichmentAnimation.fromPercent}
            toPercent={enrichmentAnimation.toPercent}
            onComplete={() => setEnrichmentAnimation(null)}
          />
        )}
        
        {/* Air Strike Animation Overlay */}
        {airStrikeAnimation && (
          <AirStrikeAnimation
            sourceCountry={airStrikeAnimation.sourceCountry}
            targetCountry={airStrikeAnimation.targetCountry}
            sourcePos={airStrikeAnimation.sourcePos}
            targetPos={airStrikeAnimation.targetPos}
            success={airStrikeAnimation.success}
            targetAADefenseLevel={airStrikeAnimation.targetAADefenseLevel}  // ⭐ NEW: Pass AA tier
            onComplete={() => {
              console.log('✈️ AIR STRIKE - Animation complete callback received');
              // Animation completed naturally - trigger cleanup from useGameActions
              if (airStrikeAnimation.onAnimationComplete) {
                airStrikeAnimation.onAnimationComplete();
              }
            }}
          />
        )}
        
        {/* Deploy Troops Animation Overlay */}
        {deployTroopsAnimation && (
          <DeployTroopsAnimation
            sourceCountry={deployTroopsAnimation.source}
            targetCountry={deployTroopsAnimation.target}
            onComplete={() => setDeployTroopsAnimation(null)}
          />
        )}
        
        {/* Enrichment Purchase Popup (for AI purchases) */}
        {enrichmentPopup && (
          <EnrichmentPurchasePopup
            {...enrichmentPopup}
            onComplete={() => setEnrichmentPopup(null)}
          />
        )}
        
        {/* AA Defense Purchase Popup (for player/AI purchases) */}
        {aaDefensePopup && (
          <AAPurchasePopup
            {...aaDefensePopup}
            onComplete={() => setAADefensePopup(null)}
          />
        )}
        
        {/* Status Report Popup */}
        {showStatusReport && (
          <div className="pointer-events-auto">
            <StatusReportPopup
              countries={game.countries}
              currentTurn={game.current_turn}
              onClose={() => {
                console.log('[APP.JS] Closing status report - setting showStatusReport to false');
                setShowStatusReport(false);
                console.log('[APP.JS] showStatusReport state updated');
              }}
            />
          </div>
        )}
        
        {/* Deploy Troops Confirmation Popup */}
        {deployTroopsConfirmation && (
          <DeployTroopsConfirmation
            sourceCountry={deployTroopsConfirmation.sourceCountry}
            targetCountry={deployTroopsConfirmation.targetCountry}
            lastDeployedTurn={game.last_deploy_turn?.[deployTroopsConfirmation.sourceCountry.code] || 0}
            currentTurn={game.current_turn}
            gameId={game.id}
            onConfirm={async () => {
              const sourceCountry = deployTroopsConfirmation.sourceCountry;
              const targetCountry = deployTroopsConfirmation.targetCountry;
              setDeployTroopsConfirmation(null);
              setIsProcessing(true);
              
              try {
                // Call backend API
                const result = await onAction('deploy_troops', targetCountry.code);
                
                // Manually trigger the Deploy Troops animation
                console.log('🪖 DEPLOY TROOPS - Setting animation state:', { source: sourceCountry.name, target: targetCountry.name });
                setDeployTroopsAnimation({
                  source: sourceCountry.name,
                  target: targetCountry.name
                });
                
                // Animation will auto-clear after 6 seconds (handled in DeployTroopsAnimation)
                // After animation, show result modal
                setTimeout(() => {
                  setDeployTroopsAnimation(null);
                  setLastAction(result);
                  setIsProcessing(false);
                }, 6000);
                
              } catch (error) {
                console.error('Deploy troops failed:', error);
                setIsProcessing(false);
              }
            }}
            onCancel={() => setDeployTroopsConfirmation(null)}
          />
        )}
        
        {/* Pause Menu - ESC to open/close */}
        {showPauseMenu && (
          <PauseMenu
            game={game}
            onSave={async () => {
              // Game is auto-saved in DB, just need to show exit screen then return to lobby
              console.log('💾 Saving and exiting to lobby...');
              
              // ⭐ Show exit screen before returning to lobby
              setShowPauseMenu(false);
              setShowExitScreen(true);
              
              // After exit screen (10s or ESC), return to lobby
              setTimeout(() => {
                setShowExitScreen(false);
                onBackToLobby(false); // Manual exit - don't count as completion
              }, 10000);
            }}
            onContinue={() => {
              setShowPauseMenu(false);
            }}
            onClose={() => {
              setShowPauseMenu(false);
            }}
            onLoadGame={async () => {
              console.log('📂 Reloading last saved game...');
              setShowPauseMenu(false);
              
              // Simply reload the page - this will trigger the game to load from the DB
              window.location.reload();
            }}
            onSurrender={handleSurrender}
          />
        )}
        
        {/* Nuclear Status Report - Mandatory every 10 turns */}
        {showNuclearStatusReport && (
          <div className="pointer-events-auto">
            <NuclearStatusReport
              countries={game.countries}
              currentTurn={game.current_turn}
              onClose={() => {
                setShowNuclearStatusReport(false);
              }}
            />
          </div>
        )}
        
        {/* Chat Bubbles - FILTER OUT DEFEATED COUNTRIES */}
        {activeChatBubbles
          .filter(bubble => !defeatedCountries.includes(bubble.countryCode))
          .map(bubble => (
          <ChatBubble
            key={bubble.id}
            countryCode={bubble.countryCode}
            message={bubble.message}
            duration={bubble.duration}
            onComplete={() => {
              setActiveChatBubbles(prev => prev.filter(b => b.id !== bubble.id));
            }}
          />
        ))}
        
        {/* Nuke Ready Celebration */}
        {nukeReadyCelebration && (
          <NukeReadyCelebration
            country={nukeReadyCelebration}
            onClose={() => setNukeReadyCelebration(null)}
          />
        )}
        
        {/* ⭐ NUKE BUTTON ANIMATION (plays BEFORE nuclear strike) */}
        {nukeButtonAnimation && (
          <NukeButtonAnimation
            sourceCountry={nukeButtonAnimation.sourceCountry}
            onComplete={handleNukeButtonComplete}
          />
        )}
        
        {/* Nuclear Strike Animation */}
        {nuclearStrikeAnimation && (
          <NuclearStrikeAnimation
            sourceCode={nuclearStrikeAnimation.sourceCode}
            targetCode={nuclearStrikeAnimation.targetCode}
            sourcePos={nuclearStrikeAnimation.sourcePos}
            targetPos={nuclearStrikeAnimation.targetPos}
            onComplete={() => {}}
          />
        )}
        
        {/* Skip AI Turns Button - Appears during AI turn processing, HIDDEN during nuke animation */}
        {isProcessingAiTurns && aiTurnsQueue.length > 0 && !nuclearStrikeAnimation && (
          <div className="fixed top-24 left-1/2 transform -translate-x-1/2 z-[70] pointer-events-auto">
            <button
              onClick={skipAiTurns}
              className="px-6 py-3 text-lg font-bold uppercase tracking-wider rounded-lg transition-all hover:scale-105 active:scale-95 animate-pulse"
              style={{
                fontFamily: "'Orbitron', sans-serif",
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.5)',
                color: '#ffffff'
              }}
            >
              ⏭️ Skip AI Turns ({aiTurnsQueue.length} remaining)
            </button>
          </div>
        )}
        
        {/* Bottom right - Global Turn display only */}
        {!propagandaAnimation && !diplomacyAnimation && !nukeReadyCelebration && !nuclearStrikeAnimation && (
          <div className="absolute bottom-4 right-4 flex flex-col gap-2 items-end" style={{ pointerEvents: 'auto' }}>
            {/* Turn display (no button) */}
            <div 
              className="bunker-panel p-4 bg-slate-900/75 backdrop-blur-md" 
              style={{ pointerEvents: 'auto' }}
              data-testid="turn-display"
            >
              <div className="text-center">
                <div className="text-xs text-muted uppercase tracking-widest">GLOBAL TURN</div>
                <div className="font-heading text-4xl text-amber">{game.current_turn}</div>
                <div className="text-xs text-muted mt-2">
                  {isProcessing || isProcessingAiTurns ? (
                    <span className="animate-pulse text-yellow-400">⏳ PROCESSING...</span>
                  ) : (
                    <span className="text-green-400">✓ Auto-Advance Enabled</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Top left - Exit to Lobby button.
            Moved from bottom-right (where it was nearly invisible at opacity-70)
            so mobile players can spot and tap it easily. Sits above the
            "GLOBAL THERMO III" title which lives in MapView2D at top-4 left-4. */}
        {!propagandaAnimation && !diplomacyAnimation && !nukeReadyCelebration && !nuclearStrikeAnimation && (
          <button
            onClick={onBackToLobby}
            className="tactical-button absolute top-4 left-4 px-3 py-2 text-xs font-bold bg-slate-900/90 backdrop-blur-md border border-red-700/60 text-red-200 hover:bg-red-900/80 hover:text-white transition-colors z-40"
            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
            data-testid="back-to-lobby-btn"
          >
            ← EXIT TO LOBBY
          </button>
        )}

        {/* Bottom panels - All UI elements at bottom to avoid overlapping with leaders */}
        {/* Hide panels during propaganda/celebration/diplomacy/nuke/nuclear strike animations */}
        {!propagandaAnimation && !diplomacyAnimation && !nukeReadyCelebration && !nuclearStrikeAnimation && (
          <>
            {/* Bottom UI Container.
                pointer-events-none on the wrapper so taps on empty space
                fall through to the map (otherwise the empty right-side
                spacer eats clicks meant for leaders). The actual
                interactive children re-enable pointer-events-auto. */}
            <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
              <div className="flex justify-between items-end gap-4">
                {/* Left side: Intelligence Feed + Player stats
                    📱 On mobile portrait, anchor these to the MAP's left
                    edge (USA region) instead of the viewport. As the user
                    swipes right to look at China/Russia, the panels slide
                    off-screen — freeing screen space. They reappear when
                    scrolling back to USA. */}
                <div
                  className="flex flex-col gap-3 w-80 pointer-events-auto transition-none"
                  style={{
                    transform: isMobilePortrait ? `translateX(${-mapScrollLeft}px)` : undefined,
                    willChange: isMobilePortrait ? 'transform' : undefined,
                  }}
                >
                  {/* Intelligence Feed - Compact and at top */}
                  <ActionLog logs={game.action_log} />
                  
                  {playerCountry && (
                    <ResourceBar
                      key={`resource-bar-${playerCountry.resources.population}-${playerCountry.resources.money}-${playerCountry.resources.influence}`}
                      resources={playerCountry.resources}
                      countryName={playerCountry.name}
                      color={playerCountry.color}
                      hasNukeReady={playerCountry.resources.nukes_ready > 0 || playerCountry.resources.uranium_enrichment >= 100}
                      game={game}
                      onRefreshGame={fetchLatestGameState}  
                      onLaunchNuke={() => {
                        if (isProcessingAiTurns) {
                          showNotification('⏳ Please wait for AI to finish their turns', 'warning');
                          return;
                        }
                        console.log('🚀 Launch Nuke button clicked - waiting for target selection');
                        setIsPendingNuclearStrike(true);
                        setSelectedCountry(null);
                        showNotification('☢️ SELECT TARGET COUNTRY on the map to launch nuclear strike', 'warning');
                      }}
                      onStatusReportClick={() => {
                        console.log('📊 Opening Status Report from ResourceBar');
                        setShowStatusReport(true);
                      }}
                    />
                  )}
                </div>

                {/* Right side spacer to maintain layout */}
                <div className="w-80"></div>
              </div>
            </div>

            {/* Center-bottom: Selected country panel */}
            {selectedCountry && (
              <>
                {/* Backdrop - Click to close */}
                <div 
                  className="fixed inset-0 z-30"
                  onClick={() => setSelectedCountry(null)}
                />
                
                {/* Panel - Centered at bottom */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 pointer-events-auto z-40">
                  <CountryPanel
                    key={`${selectedCountry.id}-${playerCountry?.resources?.money}-${playerCountry?.resources?.influence}-${playerCountry?.resources?.military}-${selectedCountry.actions_remaining}`}
                    country={selectedCountry}
                    playerCountry={playerCountry}
                    onAction={handleAction}
                    onClose={() => setSelectedCountry(null)}
                    onStatusReportClick={() => {
                      console.log('📊 Opening Status Report from CountryPanel');
                      setShowStatusReport(true);
                    }}
                    gameId={game.id}
                    game={game}
                    isProcessingAiTurns={isProcessingAiTurns}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Instructions overlay */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center pointer-events-none">
        <p className="text-xs text-muted font-mono uppercase tracking-widest">
          Click markers to select
        </p>
      </div>

      {/* Notification */}
      {notification && notification.message && notification.message.trim() !== '' && (
        <GameNotification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
      
      {/* All Country Stats Panel */}
      {showAllCountryStats && (
        <AllCountryStatsPanel
          countries={game.countries}
          onClose={() => setShowAllCountryStats(false)}
        />
      )}
    </div>
      
      {/* Action Result Modal - OLD VERSION with leader dialogue boxes */}
      {actionResult && isActionModalOpen && (
        <ActionResultModal
          result={actionResult}
          sourceCountry={actionResult.sourceCountry}
          targetCountry={actionResult.targetCountry}
          onClose={handleCloseActionModal}
        />
      )}

      {/* Victory Modal */}
      {victoryData && (
        <VictoryModal
          defeatedCountry={victoryData.defeatedCountry}
          playerCountry={victoryData.playerCountry}
          stats={victoryData.stats}
          onClose={() => setVictoryData(null)}
        />
      )}

      {/* Defeat Screen */}
      {showDefeatScreen && (
        <DefeatScreen
          game={game}
          playerCountry={playerCountry}
          onNewGame={() => {
            setShowDefeatScreen(false);
            onBackToLobby(true); // Game COMPLETED (defeat) - count it!
          }}
          onClose={() => {
            setShowDefeatScreen(false);
            onBackToLobby(true); // Game COMPLETED (defeat) - count it!
          }}
          onSpectate={() => {
            console.log('👁️ Entering spectator mode');
            setShowDefeatScreen(false);
            setIsSpectating(true); // Prevent defeat screen from re-showing
            showNotification('👁️ Spectator Mode: Watch the AI finish the war', 'info');
            // Game continues, player can watch AI turns
          }}
        />
      )}

      {/* Ad Modal - Shows between games and every 20 turns */}
      <AdModal
        isOpen={showAdModal}
        adType={adType}
        onClose={() => setShowAdModal(false)}
      />

      {/* Badge Overlay - Shows after intro for 4 seconds */}
      {showBadgeOverlay && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none"
          style={{
            animation: 'fadeInOut 4s ease-in-out forwards'
          }}
        >
          <img 
            src="/assets/gtnw3-logo.png" 
            alt="GTNW3"
            className="max-w-3xl w-full h-auto px-8"
            style={{
              filter: `
                drop-shadow(0 0 30px rgba(255, 215, 0, 0.8))
                drop-shadow(0 0 60px rgba(255, 69, 0, 0.6))
                drop-shadow(0 4px 12px rgba(0, 0, 0, 0.9))
              `,
            }}
          />
          <style>{`
            @keyframes fadeInOut {
              0% { opacity: 1; }
              75% { opacity: 1; }
              100% { opacity: 0; }
            }
          `}</style>
        </div>
      )}

      {/* 🔫 AA Defense Effect - CSS Animation */}
      {showAADefenseEffect && (
        <AADefenseEffect 
          onComplete={() => setShowAADefenseEffect(false)}
          targetCountryPosition={aaEffectTarget}
          aaPositions={aaEffectGuns}
          defendingCountry={aaEffectDefendingCountry}
          aaTier={aaEffectTier}
        />
      )}

      {/* 🛰️ Intelligence Report Modal */}
      {showIntelligenceReport && intelligenceData && (
        <IntelligenceReportModal
          intelData={intelligenceData}
          onClose={() => {
            setShowIntelligenceReport(false);
            setIntelligenceData(null);
          }}
        />
      )}

    </>
  );
}

export default GameScreen;
